/**
 * The effect vocabulary - OPBridge's software effects and a fixture's own
 * built-in FX macros, kept apart on purpose.
 *
 * ## Why the two groups must never be merged into one list
 *
 * They behave differently in the one way an operator cares about mid-set:
 *
 *   - **Software effects** are rendered by OPBridge, one task per fixture,
 *     reusing the capability resolver - so they work on ANY RGB fixture and
 *     several fixtures running the same one stay in phase. The three MUSICAL
 *     ones follow OPBridge's clock, which means they are IDLE when the
 *     transport is stopped. An operator who does not know that will tap
 *     `beat-strobe` at soundcheck, see nothing, and conclude the product is
 *     broken - so every surface offering them has to say it.
 *   - **Built-in FX macros** are channel values the FIXTURE renders. They are
 *     whatever the manufacturer built, cannot be clock-synced, and exist only
 *     on fixtures whose profile declares an FX mode (of the operator's rig,
 *     only the two PL60Cs, with seventeen macros each).
 *
 * Merging them into one dropdown would produce a list where `strobe` and
 * `police` look like peers and behave nothing alike. OPBridge split the two
 * endpoints for exactly this reason (`/dmx/fx-catalog` separate from a
 * profile's `effects`); this module keeps the split on our side rather than
 * flattening it at the UI edge.
 *
 * ## The musical list is mirrored, and that is deliberate
 *
 * `MUSICAL_EFFECTS` below duplicates OPBridge's constant. The catalogue
 * endpoint returns `free` and `musical` as separate arrays, so at runtime the
 * grouping comes from the wire and this constant is not consulted. It exists
 * for the OFFLINE case - an iPad with no bridge still has to render an
 * already-authored `beat-strobe` button with its "on the beat" note, and a
 * button that changes what it says depending on whether the bridge answered
 * is worse than one hardcoded name. Three strings that have not changed since
 * OPBridge S153; a fourth would be additive and would simply not get the note
 * offline until this is updated.
 */

import { softwareEffectsFree, softwareEffectsMusical } from '../fixtures/profileSync.ts';

/** Mirror of `opbridge_core::dmx_software_fx::MUSICAL_EFFECTS`. Offline
 *  fallback only - see this module's doc comment. */
const MUSICAL_EFFECTS = ['beat-strobe', 'bar-pulse', 'beat-cycle'];

/** True when the effect follows OPBridge's musical clock, and is therefore
 *  idle with the transport stopped. Prefers the live catalogue's grouping and
 *  falls back to the mirrored list when no bridge has answered. */
export function isMusicalEffect(effect: string): boolean {
  const slug = effect.trim();
  if (!slug) return false;
  const live = softwareEffectsMusical();
  if (live.length) return live.some((e) => e.name === slug);
  return MUSICAL_EFFECTS.includes(slug);
}

/** Title-case a slug the way OPBridge's own `fx_label` does, so
 *  `beat-strobe` reads "Beat Strobe" on both sides. Used for anything not in
 *  the live catalogue (a built-in macro name, or any effect while offline). */
export function titleCaseSlug(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** The display name for an effect. Prefers the label the catalogue sent,
 *  because OPBridge owns its own effects' names; falls back to title-casing,
 *  which is what a fixture's built-in macro gets (the profile sends bare
 *  slugs for those, with no label field). */
export function effectLabel(effect: string): string {
  const slug = effect.trim();
  if (!slug) return 'Stop FX';
  const hit = [...softwareEffectsFree(), ...softwareEffectsMusical()].find((e) => e.name === slug);
  return hit?.label ?? titleCaseSlug(slug);
}

/** True when the effect tints the operator's chosen colour rather than
 *  defining its own hue. OPBridge's rule, from `FixtureEffectRequest.color`:
 *  strobe / pulse / bar-pulse take a base colour; fire / lightning / rainbow
 *  / beat-cycle ignore it. A UI that offered a colour picker for the second
 *  group would be offering a control that does nothing, which DEC-064's
 *  "never offer what you cannot do" forbids. */
export function effectTakesColor(effect: string): boolean {
  return ['strobe', 'pulse', 'bar-pulse'].includes(effect.trim());
}
